import { useEffect } from "react";
import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n, { SUPPORTED_LANGUAGES } from "./i18n.js";
import { usePreferences, type Language } from "./preferences-store.js";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function applyDocumentLanguage(
  language: Language,
  root: Pick<HTMLElement, "lang"> | undefined = typeof document === "undefined"
    ? undefined
    : document.documentElement,
) {
  if (root) root.lang = language;
}

export function useApplyLanguage() {
  const language = usePreferences((state) => state.language);
  useEffect(() => {
    if (i18n.resolvedLanguage === language) {
      applyDocumentLanguage(language);
      return;
    }
    // O catálogo do idioma novo chega por rede (ver src/i18n.ts). changeLanguage()
    // só troca depois que ele está em memória, e o `lang` do documento acompanha:
    // anunciar o idioma novo com os textos antigos enganaria o leitor de tela.
    let applied = true;
    void i18n.changeLanguage(language).then(() => {
      if (applied) applyDocumentLanguage(language);
    });
    return () => {
      applied = false;
    };
  }, [language]);
  return language;
}

export function LanguageSubmenu() {
  const { t } = useTranslation();
  const language = usePreferences((state) => state.language);
  const setLanguage = usePreferences((state) => state.setLanguage);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Languages />
        <span className="flex-1">{t("common.language")}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup
          value={language}
          onValueChange={(value) => setLanguage(value as Language)}
        >
          {SUPPORTED_LANGUAGES.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {t(`common.languages.${option}`)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export function PublicLanguageMenu() {
  const { t } = useTranslation();
  const language = usePreferences((state) => state.language);
  const setLanguage = usePreferences((state) => state.setLanguage);
  return (
    <div className="fixed top-3 right-3 z-50">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="icon" aria-label={t("common.language")}>
            <Languages />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-44">
          <DropdownMenuRadioGroup
            value={language}
            onValueChange={(value) => setLanguage(value as Language)}
          >
            {SUPPORTED_LANGUAGES.map((option) => (
              <DropdownMenuRadioItem key={option} value={option}>
                {t(`common.languages.${option}`)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
